import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useFieldglassShiftIssuesQuery } from 'queries/admin/fieldglass/useFieldglassShiftIssuesQuery'
import { FieldglassShiftIssuesFilters } from 'pages/Fieldglass/shared/components/FieldglassIssuesFilterBar'
import { useFieldglassShiftIssuesFiltersQueryState } from '../hooks/useFieldglassShiftIssuesFiltersQueryState'

type FieldglassShiftIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useFieldglassShiftIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    filters: ReturnType<typeof useFieldglassShiftIssuesFiltersQueryState>[0]
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setFilters: (update: Partial<FieldglassShiftIssuesFilters>) => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
  }
}

const [Context, useFieldglassShiftIssuesContextValue] =
  createContext<FieldglassShiftIssuesContextState>({
    name: 'FieldglassShiftIssuesContext',
  })

export const FieldglassShiftIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useFieldglassShiftIssuesFiltersQueryState()
  const [pagination, setPagination] = usePaginationQueryState()

  const setFiltersAndResetPage = (
    update: Partial<FieldglassShiftIssuesFilters>
  ) => {
    setFilters(update)
    setPagination({ pageIndex: 0 })
  }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useFieldglassShiftIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    daysAgo: filters.daysAgo,
    companyGroupIds: filters.companyGroupIds,
    issueTypes: filters.issueTypes,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: FieldglassShiftIssuesContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      filters,
      pagination,
    },
    actions: {
      refetch,
      setFilters: setFiltersAndResetPage,
      setPagination,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useFieldglassShiftIssuesContextValue }
